<template>
  <div class="staff-employees-overview">
    <div class="toolbar">
      <span>Search by organization name</span>
      <div class="filters">
        <form class="search" @submit.prevent="onSearch">
          <input
            class="input"
            type="text"
            placeholder="Search by organization name"
            v-model="searchFilter"
          />
          <button
            class="update-button"
            type="submit"
            :disabled="organizations.refreshing || !searchFilter"
          >
            Search
          </button>
        </form>
        <button class="reset-button" @click="onSearchReset" :disabled="organizations.refreshing">
          Reset
        </button>
        <div v-if="user.isStaff && organizations.refreshing" class="organizations placeholder">
          <LoadSpinner />
        </div>
      </div>
    </div>
    <TableHeader :title="tableName" :collection="organizations" />
    <LoadSpinner v-if="organizations.refreshing" style="padding: 2rem 0" />
    <div class="table-wrapper" v-if="!organizations.refreshing">
      <md-table>
        <md-table-row class="header-row">
          <md-table-head>
            Name
            <span class="sort-icon" @click="onSortByField('name')">
              <md-icon v-if="sorts.name === null">sort</md-icon>
              <md-icon v-else-if="sorts.name === ASC">arrow_drop_down</md-icon>
              <md-icon v-else>arrow_drop_up</md-icon>
            </span>
          </md-table-head>

          <md-table-head>
            Subdomain
            <span class="sort-icon" @click="onSortByField('subdomain')">
              <md-icon v-if="sorts.subdomain === null">sort</md-icon>
              <md-icon v-else-if="sorts.subdomain === ASC">arrow_drop_down</md-icon>
              <md-icon v-else>arrow_drop_up</md-icon>
            </span>
          </md-table-head>

          <md-table-head>
            Verification
            <span class="sort-icon" @click="onSortByField('verification_method')">
              <md-icon v-if="sorts.verification_method === null">sort</md-icon>
              <md-icon v-else-if="sorts.verification_method === ASC">arrow_drop_down</md-icon>
              <md-icon v-else>arrow_drop_up</md-icon>
            </span>
          </md-table-head>

          <md-table-head>
            Grant Limit Duration
            <span class="sort-icon" @click="onSortByField('grant_limit_duration')">
              <md-icon v-if="sorts.grant_limit_duration === null">sort</md-icon>
              <md-icon v-else-if="sorts.grant_limit_duration === ASC">arrow_drop_down</md-icon>
              <md-icon v-else>arrow_drop_up</md-icon>
            </span>
          </md-table-head>
        </md-table-row>

        <md-table-row
          class="body-row"
          v-for="org in organizations.list"
          :key="org.id"
          @click.native="() => onRowClick(org)"
        >
          <md-table-cell>{{ org.name || 'Unnamed Org' }}</md-table-cell>
          <md-table-cell>{{ org.subdomain || 'unknown' }}</md-table-cell>
          <md-table-cell>{{ org.verificationMethod || 'N/A' }}</md-table-cell>
          <md-table-cell>{{ org.grantLimitDuration || 'N/A' }}</md-table-cell>

        </md-table-row>
      </md-table>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { LoadSpinner, TableHeader } from '@/components'

import Organization from '@/services/organizations'
import CollectionManager from '@/services/collectionManager'
import Pagination from '@/services/pagination'

const ASC = 'ASC'
const DESC = 'DESC'

export default {
  name: 'OrganizationOverview',
  components: {
    LoadSpinner,
    TableHeader,
  },
  data: function() {
    return {
      organizations: CollectionManager.create({
        ModelClass: Organization,
        pagination: Pagination.create({ pageSize: 40 }),
      }),
      searchFilter: '',
      tableName: '',
      sorts: {
        name: null,
        organization__name: null,
        datetime_created: null,
        is_active: null,
      },
      ASC,
      DESC,
    }
  },
  created() {
    this.refresh()
  },
  methods: {
    refresh() {
      this.organizations.refresh()
    },
    onRowClick(org) {
      const routeData = this.$router.resolve({
        name: 'StaffOrganizationDetail',
        params: {
          id: org.id,
        },
      })

      window.open(routeData.href, '_blank')
    },
    updateOrganizations(filtersObj = {}) {
      this.organizations = CollectionManager.create({
        ModelClass: Organization,
        filters: {
          ...this.organizations.filters,
          ...filtersObj,
        },
      })
      this.refresh()
    },
    onSearch() {
      this.updateOrganizations({ name: this.searchFilter })
    },
    onSearchReset() {
      this.searchFilter = ''
      this.updateOrganizations({ name: '' })
    },
    onSortByField(field) {
      // Org fields instead
      let ascending = this.sorts[field] === DESC || this.sorts[field] == null
      let newSortState = {
        name: null,
        subdomain: null,
        verification_method: null,
        grant_limit_duration: null,
      }
      newSortState[field] = ascending ? ASC : DESC
      this.sorts = newSortState
      let term = `${ascending ? '' : '-'}${field}`
      this.updateOrganizations({ ordering: term })
    },
  },
  computed: mapState(['user', 'organizationDetails']),
}
</script>

<style lang="scss" scoped>
@import '../../styles/variables';
@import '../../styles/mixins/containers';
@import '../../styles/input';
@import '../../styles/mixins/buttons';

.staff-employees-overview {
  @include screen-container($mobile-padding: 1rem 0.5rem);
}

// ToolBar Styles
.toolbar {
  margin-bottom: 2rem;
}

.filters {
  display: flex;
  flex-flow: row;
  align-items: center;

  .search {
    display: flex;
    flex-flow: row;
    align-items: center;
    width: 40rem;
  }

  .input {
    @include base-input;
    text-align: left;
    margin-right: 2rem;
  }

  .update-button {
    @include primary-button();
    min-width: unset;
    width: 6rem;
    height: 2rem;
    display: flex;
    flex-flow: row;
    justify-content: center;
    align-items: center;
  }

  .reset-button {
    @include primary-button-selected();
    min-width: unset;
    width: 6rem;
    height: 2rem;
    display: flex;
    flex-flow: row;
    justify-content: center;
    margin-left: 2rem;
    align-items: center;
  }

  .organizations {
    width: 19rem;
    height: 2.25rem;
    font-family: $base-font-family;
    font-size: 1rem;
    margin-left: auto;
    padding: 0.2rem;
  }

  .placeholder {
    width: 19rem;
    height: 2.25rem;
    font-family: $base-font-family;
    font-size: 1rem;
    border-radius: 5px;
    border: 1px solid #a6a6a6;
    background-color: #f8f8f8;
    display: flex;
    flex-flow: row;
    align-items: center;
    justify-content: center;
    margin-left: auto;
  }
}

// Table Styles
.table-header {
  display: flex;
  margin-bottom: 1rem;

  &__start {
    display: flex;
    padding: 1rem 0 0 0;
  }

  &__end {
    flex: 4;
    align-self: flex-end;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
}

.sort-icon {
  &:hover {
    cursor: pointer;
  }
}

.table-wrapper {
  max-width: 100%;
  overflow-x: auto;
}

.header-row {
  background-color: $soft-gray;

  & > .md-table-head {
    font-size: 1rem;
  }
}

.body-row {
  & > .md-table-cell {
    border-top: 1px solid $soft-gray;
    padding: 0.5rem;
  }

  &:hover {
    background-color: $off-white;
    cursor: pointer;
  }
}
</style>
